import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ProfileOutlined,
  ThunderboltOutlined,
  PauseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';

import {
  fetchSessions,
  fetchKlasses,
  fetchPersons,
  selectors,
  Session,
  PLAYING,
  PAUSED,
  FINISHED,
} from 'state';

import Sidecar from './Sidecar';
import SessionCard from './SessionCard';
import {
  StatusFilter,
  TypeFilter,
  matchesQuery,
  matchesStatus,
  matchesType,
  isFinished,
  splitGroups,
} from './helpers';
import './dashboardV2.css';

const SessionsV2: React.FC = () => {
  const dispatch = useDispatch();
  const sessions = useSelector(selectors.sessions) as Session[];
  const username = useSelector(selectors.username);
  const selectedPerson = useSelector(selectors.selectedPerson);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [type, setType] = useState<TypeFilter>('all');
  const [focusedId, setFocusedId] = useState<number | null>(null);

  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const panelBodyRef = useRef<HTMLDivElement | null>(null);

  // Same bootstrap as the classic view — GET /sessions carries everything a
  // card needs; klasses/persons are fetched for parity (persona picker + any
  // co-located reads). No in-session/performance fetch happens here.
  useEffect(() => {
    dispatch(fetchSessions({}));
    dispatch(fetchKlasses({}));
    dispatch(fetchPersons({}));
  }, [dispatch]);

  const firstName = String(selectedPerson?.name || username || 'Trader').split(
    ' ',
  )[0];

  const q = query.trim().toLowerCase();

  // Faceted counts: each facet counts over the OTHER active filters + search,
  // so a facet's numbers stay meaningful as you toggle the other.
  const queryOnly = useMemo(
    () => sessions.filter(s => matchesQuery(s, q)),
    [sessions, q],
  );
  const statusCounts = useMemo(() => {
    const base = queryOnly.filter(s => matchesType(s, type));
    return {
      all: base.length,
      a: base.filter(s => !isFinished(s)).length,
      b: base.filter(isFinished).length,
    };
  }, [queryOnly, type]);
  const typeCounts = useMemo(() => {
    const base = queryOnly.filter(s => matchesStatus(s, status));
    return {
      all: base.length,
      a: base.filter(s => s.scenario_type === 1).length,
      b: base.filter(s => s.scenario_type === 0).length,
    };
  }, [queryOnly, status]);

  const filtered = useMemo(
    () =>
      sessions.filter(
        s =>
          matchesQuery(s, q) &&
          matchesStatus(s, status) &&
          matchesType(s, type),
      ),
    [sessions, q, status, type],
  );

  const groups = useMemo(() => splitGroups(filtered), [filtered]);
  const flatList = useMemo(
    () => [...groups.active, ...groups.finished],
    [groups],
  );

  // Hero KPIs — over ALL sessions, independent of filters.
  const kpis = useMemo(() => {
    const running = sessions.filter(s => s.status === PLAYING).length;
    const paused = sessions.filter(s => s.status === PAUSED).length;
    const finishedN = sessions.filter(s => s.status === FINISHED).length;
    return [
      { icon: <ProfileOutlined />, label: 'Sessions', value: sessions.length },
      { icon: <ThunderboltOutlined />, label: 'Running', value: running },
      { icon: <PauseCircleOutlined />, label: 'Paused', value: paused },
      { icon: <CheckCircleOutlined />, label: 'Finished', value: finishedN },
    ];
  }, [sessions]);

  const registerRef = (id: number, el: HTMLDivElement | null) => {
    if (el) cardRefs.current.set(id, el);
    else cardRefs.current.delete(id);
  };

  // Quick-nav: clicking a rail row scrolls that card into view WITHIN the panel
  // body (the frame is viewport-locked, so the page never scrolls).
  useEffect(() => {
    if (focusedId == null) return;
    const el = cardRefs.current.get(focusedId);
    const body = panelBodyRef.current;
    if (el && body) {
      body.scrollTo({ top: Math.max(0, el.offsetTop - 8), behavior: 'smooth' });
    }
  }, [focusedId]);

  const renderGroup = (label: string, rows: Session[]) =>
    rows.length > 0 && (
      <>
        <div className="dv-group__label">{label}</div>
        <div className="dv-grid">
          {rows.map(s => (
            <SessionCard
              key={s.id}
              session={s}
              focused={focusedId === s.id}
              registerRef={registerRef}
            />
          ))}
        </div>
      </>
    );

  return (
    <div className="dv-root">
      {/* ---- Hero ---- */}
      <header className="dv-hero">
        <div className="dv-hero__main">
          <span className="dv-eyebrow">
            Portfolio Management · Trainee Desk
          </span>
          <h1 className="dv-title">Welcome back, {firstName}</h1>
          <div className="dv-kpis">
            {kpis.map(k => (
              <div className="dv-kpi" key={k.label}>
                <span className="dv-kpi__icon">{k.icon}</span>
                <span className="dv-kpi__v">{k.value}</span>
                <span className="dv-kpi__l">{k.label}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ---- Master-detail: sidecar + workspace ---- */}
      <div className="dv-md">
        <Sidecar
          totalCount={sessions.length}
          query={query}
          onQuery={setQuery}
          status={status}
          onStatus={setStatus}
          statusCounts={statusCounts}
          type={type}
          onType={setType}
          typeCounts={typeCounts}
          list={flatList}
          focusedId={focusedId}
          onFocus={setFocusedId}
        />

        <section className="dv-panel">
          <div className="dv-panel__head">
            <div>
              <span className="dv-eyebrow">Workspace</span>
              <div className="dv-panel__title">
                {filtered.length} of {sessions.length} session
                {sessions.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>
          <div className="dv-panel__body" ref={panelBodyRef}>
            {sessions.length === 0 ? (
              <div className="dv-empty">
                <ProfileOutlined className="dv-empty__icon" />
                <div className="dv-empty__title">No sessions yet</div>
                <div className="dv-empty__sub">
                  When a trainer adds you to a session, it will appear here.
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="dv-empty">
                <div className="dv-empty__title">No matches</div>
                <div className="dv-empty__sub">
                  No sessions match the current search and filters.
                </div>
              </div>
            ) : (
              <>
                {renderGroup('Active', groups.active)}
                {renderGroup('Finished', groups.finished)}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SessionsV2;
